import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Checkbox,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  Delete,
  Download,
  QrCode,
  Refresh,
  Search,
  FilterList,
  Close
} from '@mui/icons-material';
import StatusTracker from './StatusTracker';
import EInvoiceService from '../../services/EInvoiceService';

const EInvoiceManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [eInvoices, setEInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEInvoice, setSelectedEInvoice] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const statusOptions = ['All', 'Draft', 'Ready', 'Submitted', 'Valid', 'Cancelled', 'Failed'];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Valid':
        return 'success';
      case 'Failed':
        return 'error';
      case 'Cancelled':
        return 'warning';
      case 'Submitted':
        return 'primary';
      case 'Ready':
        return 'info';
      default:
        return 'default';
    }
  };

  const loadEInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await EInvoiceService.getAllEInvoices();
      // Ensure eInvoices is always an array to prevent .filter() errors
      const invoices = Array.isArray(response) ? response : (response?.data || []);
      setEInvoices(Array.isArray(invoices) ? invoices : []);
    } catch (err) {
      setError('Failed to load e-invoices');
      console.error('Error loading e-invoices:', err);
      setEInvoices([]); // Fallback to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const refreshEInvoices = async () => {
    try {
      setRefreshing(true);
      await loadEInvoices();
    } catch (err) {
      console.error('Error refreshing e-invoices:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewStatus = (eInvoice) => {
    setSelectedEInvoice(eInvoice);
    setStatusDialogOpen(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const allIds = filteredEInvoices.map((invoice) => (
      invoice.eInvoiceId || invoice.EInvoiceId || invoice.EInvoiceDocumentId
    ));
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.length === 0) return;
    try {
      setBulkSubmitting(true);
      setError(null);
      await EInvoiceService.bulkSubmit(selectedIds);
      await refreshEInvoices();
      setSelectedIds([]);
    } catch (err) {
      setError('Failed to submit selected e-invoices');
      console.error('Error bulk submitting:', err);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleStatusUpdate = (newStatus) => {
    // Update the e-invoice status in the local state
    setEInvoices(prev => 
      prev.map(invoice => 
        invoice.EInvoiceDocumentId === selectedEInvoice.EInvoiceDocumentId
          ? { ...invoice, Status: newStatus }
          : invoice
      )
    );
  };

  const handleDownload = async (eInvoiceId) => {
    try {
      await EInvoiceService.downloadEInvoice(eInvoiceId);
    } catch (err) {
      setError('Failed to download e-invoice');
      console.error('Error downloading e-invoice:', err);
    }
  };

  const handleGenerateQR = async (eInvoiceId) => {
    try {
      const response = await EInvoiceService.generateQRCode(eInvoiceId);
      // You could show a dialog with the QR code here
      console.log('QR Code generated:', response);
    } catch (err) {
      setError('Failed to generate QR code');
      console.error('Error generating QR code:', err);
    }
  };

  const filteredEInvoices = eInvoices.filter(invoice => {
    const docNumber = invoice.documentNumber || invoice.DocumentNumber;
    const recipient = invoice.recipient || invoice.Recipient;
    const recipientName = recipient
      ? (recipient.companyName || recipient.CompanyName || `${(recipient.firstName || recipient.FirstName || '')} ${(recipient.lastName || recipient.LastName || '')}`.trim())
      : '';
    const recipientTin = recipient?.recipientTIN || recipient?.RecipientTIN || '';

    const matchesSearch =
      (docNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (recipientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (recipientTin || '').toLowerCase().includes(searchTerm.toLowerCase());

    const status = invoice.status || invoice.Status;
    const matchesStatus = statusFilter === 'All' || status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    loadEInvoices();
  }, []);

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`einvoice-tabpanel-${index}`}
      aria-labelledby={`einvoice-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" fontWeight="bold">
              E-Invoice Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {/* Navigate to create e-invoice */}}
            >
              Create E-Invoice
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            TCM services do not allow consolidated invoices. Submitting multiple selections sends separate invoices.
          </Alert>

          <Box display="flex" justifyContent="flex-end" mb={2} gap={1}>
            <Button
              variant="contained"
              color="primary"
              disabled={selectedIds.length === 0 || bulkSubmitting}
              onClick={handleBulkSubmit}
            >
              {bulkSubmitting ? 'Submitting...' : `Submit Selected (${selectedIds.length})`}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="All E-Invoices" />
            <Tab label="Dashboard" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {/* Search and Filter Controls */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by document number, recipient name, or TIN..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Status Filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  InputProps={{
                    startAdornment: <FilterList sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={refreshEInvoices}
                  disabled={refreshing}
                  sx={{ height: '56px' }}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>

            {/* E-Invoice Table */}
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedIds.length > 0 && selectedIds.length < filteredEInvoices.length}
                          checked={filteredEInvoices.length > 0 && selectedIds.length === filteredEInvoices.length}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Document Number</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEInvoices.map((invoice) => {
                      const id = invoice.eInvoiceId || invoice.EInvoiceId || invoice.EInvoiceDocumentId;
                      const docNumber = invoice.documentNumber || invoice.DocumentNumber;
                      const recipient = invoice.recipient || invoice.Recipient;
                      const recipientName = recipient
                        ? (recipient.companyName || recipient.CompanyName || `${(recipient.firstName || recipient.FirstName || '')} ${(recipient.lastName || recipient.LastName || '')}`.trim())
                        : 'N/A';
                      const amount = invoice.totalAmount || invoice.TotalAmount;
                      const status = invoice.status || invoice.Status;
                      const createdAt = invoice.createdAt || invoice.CreatedAt;
                      return (
                      <TableRow key={id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(id)}
                            onChange={() => toggleSelect(id)}
                          />
                        </TableCell>
                         <TableCell>
                           <Typography variant="body2" fontWeight="medium">
                             {docNumber}
                           </Typography>
                         </TableCell>
                         <TableCell>
                           <Box>
                             <Typography variant="body2">
                               {recipientName}
                             </Typography>
                             <Typography variant="caption" color="text.secondary">
                               {recipient?.recipientTIN || recipient?.RecipientTIN || 'No TIN'}
                             </Typography>
                           </Box>
                         </TableCell>
                         <TableCell>
                           <Typography variant="body2">
                             {typeof amount === 'number' ? `RM ${amount.toFixed(2)}` : 'N/A'}
                           </Typography>
                         </TableCell>
                         <TableCell>
                           <Chip
                             label={status}
                             color={getStatusColor(status)}
                             size="small"
                           />
                         </TableCell>
                         <TableCell>
                           <Typography variant="body2">
                             {createdAt ? new Date(createdAt).toLocaleDateString() : '—'}
                           </Typography>
                         </TableCell>
                         <TableCell align="center">
                           <Box display="flex" gap={1}>
                             <Tooltip title="View Status">
                               <IconButton
                                 size="small"
                                 onClick={() => handleViewStatus(invoice)}
                               >
                                 <Visibility />
                               </IconButton>
                             </Tooltip>
                             <Tooltip title="Download">
                               <IconButton
                                 size="small"
                                 onClick={() => handleDownload(id)}
                               >
                                 <Download />
                               </IconButton>
                             </Tooltip>
                             <Tooltip title="Generate QR">
                               <IconButton
                                 size="small"
                                 onClick={() => handleGenerateQR(id)}
                               >
                                 <QrCode />
                               </IconButton>
                             </Tooltip>
                           </Box>
                         </TableCell>
                      </TableRow>
                    )})}
                    {filteredEInvoices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" py={4}>
                            No e-invoices found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Dashboard content can be added here */}
            <Typography variant="h6" color="text.secondary">
              Dashboard coming soon...
            </Typography>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              E-Invoice Status - {(selectedEInvoice?.documentNumber || selectedEInvoice?.DocumentNumber)}
            </Typography>
            <IconButton onClick={() => setStatusDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEInvoice && (
            <StatusTracker
              eInvoiceId={selectedEInvoice.eInvoiceId || selectedEInvoice.EInvoiceId || selectedEInvoice.EInvoiceDocumentId}
              onStatusUpdate={handleStatusUpdate}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Action Button for Quick Create */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={() => {/* Navigate to create e-invoice */}}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default EInvoiceManagement;